import React from "react";
import { useMarket } from "../contexts/MarketContext";
import useGlobalSummary from "../useGlobalSummary";
import MarketsMap from "./MarketsMap";
import Scrollable from "../shared/Scrollable";
import { usePreference } from "../contexts/PreferencesContext";


const MarketsAttention = ({ accountId, mode }) => {
  const { navigateToMarket } = useMarket();
  const { data, loading, error } = useGlobalSummary();
  // Remembered per user: however you last read this panel, it opens that way.
  const [view, setView] = usePreference("marketsView", "table"); // 'table' | 'map'

  // Rows come from the live roll-up. They used to be a literal array that
  // listed DE, which is not a deployed market, with figures unrelated to
  // anything the API returns.
  const marketData = (data?.markets || []).map((m, index) => ({
    rank: index + 1,
    market: m.code,
    health: m.healthPercent === null ? "—" : `${m.healthPercent}%`,
    // Kept raw alongside the formatted string: the map bands on the number.
    healthPercent: m.healthPercent,
    // No source for a delta: the market endpoints report current state only,
    // so this stays blank until history is stored somewhere.
    change: "",
    stores: m.stores,
    // Set when the store column came back holding something that is not a
    // store, or when only affected stores were returned.
    suspect: m.storeIdValidity < 0.9 || m.coverage !== "full",
  }));

  const clickable = mode === "global";

  const handleMarketClick = (marketCode) => {
    console.log(`[MarketsAttention] Market clicked: ${marketCode}`);
    navigateToMarket(marketCode);
  };

  return (
    <div className="global-card">
      <div className="global-card-header">
        <h2>Markets Requiring Attention</h2>
        {data?.global && !data.global.storeTotalTrustworthy ? (
          <span
            className="oh-small"
            title={`Suspect: ${(data.global.partialMarkets || []).join(", ")}`}
          >
            ⚠️ store counts unverified
          </span>
        ) : null}
      </div>

      <div className="oh-tabs">
        <button
          type="button"
          className={`oh-tab ${view === "table" ? "active" : ""}`}
          onClick={() => setView("table")}
          aria-pressed={view === "table"}
        >
          Table
        </button>
        <button
          type="button"
          className={`oh-tab ${view === "map" ? "active" : ""}`}
          onClick={() => setView("map")}
          aria-pressed={view === "map"}
        >
          Map
        </button>
      </div>

      {loading ? <p className="oh-small">Aggregating markets...</p> : null}
      {error ? (
        <p className="oh-small">Market roll-up unavailable: {error.message}</p>
      ) : null}

      {view === "map" ? (
        <MarketsMap
          markets={marketData}
          onMarketClick={clickable ? handleMarketClick : undefined}
        />
      ) : (
        // Scrolls inside the card rather than growing it, so the row keeps the
        // height the chart beside it sets however many markets there are.
        <Scrollable className="global-card-scroll">
          <table className="global-table global-table--sticky">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Market</th>
                <th>Health</th>
                <th>Change</th>
                <th>Stores</th>
              </tr>
            </thead>

          <tbody>
            {marketData.map((item) => (
              <tr
                key={item.market}
                // The whole row opens the market. The button below stays for
                // keyboard and screen readers — a row is not focusable, and
                // making the <tr> itself a button would take the cells out of
                // the table semantics that make the numbers readable.
                className={clickable ? "global-row-clickable" : undefined}
                onClick={clickable ? () => handleMarketClick(item.market) : undefined}
              >
                <td>{item.rank}</td>
                <td>
                  {clickable ? (
                    <button
                      type="button"
                      className="global-market-link"
                      // Without this the row handler fires too, and the market
                      // is navigated to twice on every keyboard activation.
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMarketClick(item.market);
                      }}
                    >
                      {item.market}
                    </button>
                  ) : (
                    item.market
                  )}
                </td>
                <td>{item.health}</td>
                <td
                  className={
                    item.change.startsWith("-")
                      ? "global-negative"
                      : "global-positive"
                  }
                >
                  {item.change || "—"}
                </td>
                <td>
                  {item.stores}
                  {item.suspect ? (
                    <span
                      className="oh-small"
                      title="Only part of this market was returned, or the store column held non-store values."
                      style={{ marginLeft: "0.35rem" }}
                    >
                      ?
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </Scrollable>
      )}
    </div>
  );
};

export default MarketsAttention;
